// ISO 286 Toleranzen und Passungen - Datenbasis für App und Passungsrechner.
// Aufbau wie ISO 286-1: Grundtoleranzen (IT) und Grundabmaße der Wellen als
// Tabellen, Bohrungs-Grundabmaße nach den Regeln der Norm abgeleitet.
// Werte in µm, Ergebnisse der öffentlichen Funktionen in mm.
// Referenzwerte und Gegenprobe: siehe *.test.js und __fixtures__/.
// Keine Abhängigkeiten - läuft in App, Node und auf der Landing.

// Obergrenzen der Nennmaßbereiche "über … bis" in mm.
export const BEREICHE_IT = [3, 6, 10, 18, 30, 50, 80, 120, 180, 250, 315, 400, 500];
export const BEREICHE_GA = [3, 6, 10, 14, 18, 24, 30, 40, 50, 65, 80, 100, 120,
  140, 160, 180, 200, 225, 250, 280, 315, 355, 400, 450, 500];

export function bereichIndex(n, grenzen) {
  const v = Math.abs(Number(n));
  if (!Number.isFinite(v) || v <= 0 || v > grenzen[grenzen.length - 1]) return -1;
  return grenzen.findIndex((g) => v <= g);
}

// Grundtoleranzen in µm, je Grad 13 Werte (BEREICHE_IT).
const IT = {
  1:  [0.8, 1, 1, 1.2, 1.5, 1.5, 2, 2.5, 3.5, 4.5, 6, 7, 8],
  2:  [1.2, 1.5, 1.5, 2, 2.5, 2.5, 3, 4, 5, 7, 8, 9, 10],
  3:  [2, 2.5, 2.5, 3, 4, 4, 5, 6, 8, 10, 12, 13, 15],
  4:  [3, 4, 4, 5, 6, 7, 8, 10, 12, 14, 16, 18, 20],
  5:  [4, 5, 6, 8, 9, 11, 13, 15, 18, 20, 23, 25, 27],
  6:  [6, 8, 9, 11, 13, 16, 19, 22, 25, 29, 32, 36, 40],
  7:  [10, 12, 15, 18, 21, 25, 30, 35, 40, 46, 52, 57, 63],
  8:  [14, 18, 22, 27, 33, 39, 46, 54, 63, 72, 81, 89, 97],
  9:  [25, 30, 36, 43, 52, 62, 74, 87, 100, 115, 130, 140, 155],
  10: [40, 48, 58, 70, 84, 100, 120, 140, 160, 185, 210, 230, 250],
  11: [60, 75, 90, 110, 130, 160, 190, 220, 250, 290, 320, 360, 400],
  12: [100, 120, 150, 180, 210, 250, 300, 350, 400, 460, 520, 570, 630],
  13: [140, 180, 220, 270, 330, 390, 460, 540, 630, 720, 810, 890, 970],
  14: [250, 300, 360, 430, 520, 620, 740, 870, 1000, 1150, 1300, 1400, 1550],
  15: [400, 480, 580, 700, 840, 1000, 1200, 1400, 1600, 1850, 2100, 2300, 2500],
  16: [600, 750, 900, 1100, 1300, 1600, 1900, 2200, 2500, 2900, 3200, 3600, 4000],
  17: [1000, 1200, 1500, 1800, 2100, 2500, 3000, 3500, 4000, 4600, 5200, 5700, 6300],
  18: [1400, 1800, 2200, 2700, 3300, 3900, 4600, 5400, 6300, 7200, 8100, 8900, 9700],
};

/** Grundtoleranz in µm, oder null (Grad unbekannt / Nennmaß außerhalb). */
export function it(grad, n) {
  const zeile = IT[grad];
  const i = bereichIndex(n, BEREICHE_IT);
  return zeile && i >= 0 ? zeile[i] : null;
}
// Wellen-Grundabmaße in µm je BEREICHE_GA (25 Werte).
// a-h: oberes Abmaß es; j/k/m-x: unteres Abmaß ei.
const WELLE_ES = {
  a: [-270, -270, -280, -290, -290, -300, -300, -310, -320, -340, -360, -380, -410, -460, -520, -580, -660, -740, -820, -920, -1050, -1200, -1350, -1500, -1650],
  c: [-60, -70, -80, -95, -95, -110, -110, -120, -130, -140, -150, -170, -180, -200, -210, -230, -240, -260, -280, -300, -330, -360, -400, -440, -480],
  d: [-20, -30, -40, -50, -50, -65, -65, -80, -80, -100, -100, -120, -120, -145, -145, -145, -170, -170, -170, -190, -190, -210, -210, -230, -230],
  e: [-14, -20, -25, -32, -32, -40, -40, -50, -50, -60, -60, -72, -72, -85, -85, -85, -100, -100, -100, -110, -110, -125, -125, -135, -135],
  f: [-6, -10, -13, -16, -16, -20, -20, -25, -25, -30, -30, -36, -36, -43, -43, -43, -50, -50, -50, -56, -56, -62, -62, -68, -68],
  g: [-2, -4, -5, -6, -6, -7, -7, -9, -9, -10, -10, -12, -12, -14, -14, -14, -15, -15, -15, -17, -17, -18, -18, -20, -20],
  h: Array(25).fill(0),
};
const WELLE_EI = {
  j56: [-2, -2, -2, -3, -3, -4, -4, -5, -5, -7, -7, -9, -9, -11, -11, -11, -13, -13, -13, -16, -16, -18, -18, -20, -20],
  j7: [-4, -4, -5, -6, -6, -8, -8, -10, -10, -12, -12, -15, -15, -18, -18, -18, -21, -21, -21, -26, -26, -28, -28, -32, -32],
  k: [0, 1, 1, 1, 1, 2, 2, 2, 2, 2, 2, 3, 3, 3, 3, 3, 4, 4, 4, 4, 4, 4, 4, 5, 5],
  m: [2, 4, 6, 7, 7, 8, 8, 9, 9, 11, 11, 13, 13, 15, 15, 15, 17, 17, 17, 20, 20, 21, 21, 23, 23],
  n: [4, 8, 10, 12, 12, 15, 15, 17, 17, 20, 20, 23, 23, 27, 27, 27, 31, 31, 31, 34, 34, 37, 37, 40, 40],
  p: [6, 12, 15, 18, 18, 22, 22, 26, 26, 32, 32, 37, 37, 43, 43, 43, 50, 50, 50, 56, 56, 62, 62, 68, 68],
  r: [10, 15, 19, 23, 23, 28, 28, 34, 34, 41, 43, 51, 54, 63, 65, 68, 77, 80, 84, 94, 98, 108, 114, 126, 132],
  s: [14, 19, 23, 28, 28, 35, 35, 43, 43, 53, 59, 71, 79, 92, 100, 108, 122, 130, 140, 158, 170, 190, 208, 232, 252],
  u: [18, 23, 28, 33, 33, 41, 48, 60, 70, 87, 102, 124, 144, 170, 190, 210, 236, 258, 284, 315, 350, 390, 435, 490, 540],
  x: [20, 28, 34, 40, 45, 54, 64, 80, 97, 122, 146, 178, 210, 248, 280, 310, 350, 385, 425, 475, 525, 590, 660, 740, 820],
};

// Genormte Grade je Grundabmaß (ISO 286-1, "genormte Grundtoleranzgrade").
const range = (a, b) => Array.from({ length: b - a + 1 }, (_, i) => a + i);
const GRADE_WELLE = {
  a: range(9, 13), c: range(8, 12), d: range(5, 13), e: range(5, 10), f: range(3, 10), g: range(3, 10),
  h: range(1, 18), js: range(1, 18), j: [5, 6, 7], k: range(3, 13), m: range(3, 9), n: range(3, 9),
  p: range(3, 10), r: range(3, 10), s: range(3, 10), u: [6, 7, 8], x: [6, 7, 8],
};

const KLASSE_RE = /^(JS|js|[A-Za-z])(\d{1,2})$/;

/** "H7" → { art, buchstabe, grad } oder null. Groß = Bohrung, klein = Welle. */
export function parseKlasse(s) {
  const m = typeof s === 'string' ? KLASSE_RE.exec(s) : null;
  if (!m) return null;
  const buchstabe = m[1];
  if (buchstabe !== 'JS' && buchstabe !== 'js' && buchstabe.length !== 1) return null;
  return { art: buchstabe[0] === buchstabe[0].toUpperCase() ? 'bohrung' : 'welle', buchstabe, grad: Number(m[2]) };
}

/** Grundabmaße einer Welle in µm ({es, ei}) oder null. Intern, für Tests exportiert. */
export function wellenGrundabmass(b, grad, n) {
  const i = bereichIndex(n, BEREICHE_GA);
  const t = it(grad, n);
  if (i < 0 || t == null) return null;
  if (WELLE_ES[b]) return { es: WELLE_ES[b][i], ei: WELLE_ES[b][i] - t };
  if (b === 'js') return { es: t / 2, ei: -t / 2 };
  let ei;
  if (b === 'j') ei = (grad === 7 ? WELLE_EI.j7 : WELLE_EI.j56)[i];
  else if (b === 'k') ei = grad >= 4 && grad <= 7 ? WELLE_EI.k[i] : 0;
  else if (WELLE_EI[b]) ei = WELLE_EI[b][i];
  else return null;
  return { es: ei + t, ei };
}

// µm → mm, auf 0,0001 mm. "+ 0" macht aus -0 (Vorzeichenumkehr bei
// Bohrungen, z. B. EI von H = -0) eine echte 0.
const mm = (um) => Math.round(um * 10) / 10000 + 0;

// J (Bohrung): oberes Abmaß ES in µm je BEREICHE_GA, für J6, J7, J8.
// J6/J7 aus den Grenzabmaßen nach ISO 286-2, J8 aus den Grundabmaßen.
const J_ES = {
  6: [2, 5, 5, 6, 6, 8, 8, 10, 10, 13, 13, 16, 16, 18, 18, 18, 22, 22, 22, 25, 25, 29, 29, 33, 33],
  7: [4, 6, 8, 10, 10, 12, 12, 14, 14, 18, 18, 22, 22, 26, 26, 26, 30, 30, 30, 36, 36, 39, 39, 43, 43],
  8: [6, 10, 12, 15, 15, 20, 20, 24, 24, 28, 28, 34, 34, 41, 41, 41, 47, 47, 47, 55, 55, 60, 60, 66, 66],
};
// Δ in µm für IT3-IT8 je BEREICHE_IT (bis 3 mm: 0).
const DELTA = {
  3: [0, 1, 1, 1, 1.5, 1.5, 2, 2, 3, 3, 4, 4, 5],
  4: [0, 1.5, 1.5, 2, 2, 3, 3, 4, 4, 4, 4, 5, 5],
  5: [0, 1, 2, 3, 3, 4, 5, 5, 6, 6, 7, 7, 7],
  6: [0, 3, 3, 3, 4, 5, 6, 7, 7, 9, 9, 11, 13],
  7: [0, 4, 6, 7, 8, 9, 11, 13, 15, 17, 20, 21, 23],
  8: [0, 6, 7, 9, 12, 14, 16, 19, 23, 26, 29, 32, 34],
};
const delta = (grad, n) => DELTA[grad]?.[bereichIndex(n, BEREICHE_IT)] ?? 0;

const GRADE_BOHRUNG = {
  A: range(9, 13), C: range(8, 13), D: range(6, 13), E: range(5, 10), F: range(3, 10), G: range(3, 10),
  H: range(1, 18), JS: range(1, 18), J: [6, 7, 8], K: range(3, 10), M: range(3, 10), N: range(3, 11),
  P: range(3, 10), R: range(3, 10), S: range(3, 10), U: [6, 7, 8], X: [6, 7, 8],
};

// Ausnahmen der Norm, die die allgemeinen Regeln nicht abbilden (Index =
// BEREICHE_GA). ISO 286-1: M6 bei 250-315 mm ES = -9 µm statt -m + Δ = -11.
// Gefunden über die Gegenprobe gegen die gedruckten Grenzabmaße (Tests).
const AUSNAHMEN = { M6: { 19: { ES: -9 }, 20: { ES: -9 } } };

/** Grundabmaße einer Bohrung in µm ({ES, EI}) nach ISO 286-1. */
export function bohrungsGrundabmass(B, grad, n) {
  const i = bereichIndex(n, BEREICHE_GA);
  const t = it(grad, n);
  if (i < 0 || t == null) return null;
  const welle = B.toLowerCase();
  let ES;
  if ('ACDEFGH'.includes(B) && B.length === 1) {
    const EI = 0 - WELLE_ES[welle][i]; // 0 - x statt -x: kein -0 bei H
    return { ES: EI + t, EI };
  } else if (B === 'JS') {
    return { ES: t / 2, EI: -t / 2 };
  } else if (B === 'J') {
    ES = J_ES[grad]?.[i];
  } else if (B === 'K') {
    ES = grad <= 8 ? -WELLE_EI.k[i] + delta(grad, n) : 0;
  } else if (B === 'M') {
    ES = grad <= 8 ? -WELLE_EI.m[i] + delta(grad, n) : -WELLE_EI.m[i];
  } else if (B === 'N') {
    ES = grad <= 8 ? -WELLE_EI.n[i] + delta(grad, n) : (i === 0 ? -4 : 0);
  } else if ('PRSUX'.includes(B) && B.length === 1) {
    ES = -WELLE_EI[welle][i] + (grad <= 7 ? delta(grad, n) : 0);
  }
  if (ES == null) return null;
  const ausnahme = AUSNAHMEN[`${B}${grad}`]?.[i];
  if (ausnahme) ES = ausnahme.ES;
  return { ES, EI: ES - t };
}

export function grenzabmasse(n, klasse) {
  const k = parseKlasse(klasse);
  const grade = k && (k.art === 'welle' ? GRADE_WELLE : GRADE_BOHRUNG)[k.buchstabe];
  if (!grade || !grade.includes(k.grad)) return { nichtGenormt: true };
  if (bereichIndex(n, BEREICHE_GA) < 0) return { ausserhalb: true };
  if (k.art === 'welle') {
    const g = wellenGrundabmass(k.buchstabe, k.grad, n);
    return { oben: mm(g.es), unten: mm(g.ei), toleranz: mm(g.es - g.ei) };
  }
  const g = bohrungsGrundabmass(k.buchstabe, k.grad, n);
  return { oben: mm(g.ES), unten: mm(g.EI), toleranz: mm(g.ES - g.EI) };
}

/** Passung Bohrung/Welle: Höchst-/Mindestspiel in mm (negativ = Übermaß). */
export function passung(n, bohrung, welle) {
  const kb = parseKlasse(bohrung);
  const kw = parseKlasse(welle);
  if (!kb || kb.art !== 'bohrung') return { nichtGenormt: true, klasse: bohrung };
  if (!kw || kw.art !== 'welle') return { nichtGenormt: true, klasse: welle };
  const b = grenzabmasse(n, bohrung);
  const w = grenzabmasse(n, welle);
  if (b.nichtGenormt) return { nichtGenormt: true, klasse: bohrung };
  if (w.nichtGenormt) return { nichtGenormt: true, klasse: welle };
  if (b.ausserhalb || w.ausserhalb) return { ausserhalb: true };
  const hoechstspiel = mm((b.oben - w.unten) * 1000);
  const mindestspiel = mm((b.unten - w.oben) * 1000);
  const art = mindestspiel >= 0 ? 'spiel' : hoechstspiel <= 0 ? 'uebermass' : 'uebergang';
  return { hoechstspiel, mindestspiel, art, bohrung: b, welle: w };
}

const REIHENFOLGE = ['A', 'C', 'D', 'E', 'F', 'G', 'H', 'JS', 'J', 'K', 'M', 'N', 'P', 'R', 'S', 'U', 'X'];

/** Alle genormten Klassen, sortiert nach Grundabmaß und Grad. */
export function klassen() {
  const liste = (grade, gross) => REIHENFOLGE.flatMap((B) => {
    const b = gross ? B : B.toLowerCase();
    return (grade[b] || []).map((g) => `${b}${g}`);
  });
  return { bohrungen: liste(GRADE_BOHRUNG, true), wellen: liste(GRADE_WELLE, false) };
}
